import React, { useContext, Suspense } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import PrivateRoute from './PrivateRoute';  
import { UserProvider } from './contexts/UserContext'; 
import { MembershipProvider, MembershipContext } from './contexts/MembershipContext';

import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import DelayedFallback from './components/DelayedFallback'; // Ajusta la ruta si es necesario

import membresias from './data/membresias.json';

// Componentes de página
const CreatePasswordPage = React.lazy(() => import('./pages/CreatePasswordPage'));
const NoDisponibleDemoPage = React.lazy(() => import('./pages/NoDisponibleDemoPage'));
const DemoPage = React.lazy(() => import('./pages/DemoPage'));
const LoginPage = React.lazy(() => import('./pages/LoginPage'));
const MigrationPage = React.lazy(() => import('./pages/MigrationPage'));
const ForgotPasswordPage = React.lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = React.lazy(() => import('./pages/ResetPasswordPage'));
const LogoutPage = React.lazy(() => import('./pages/LogoutPage'));
const DashboardPage = React.lazy(() => import('./pages/DashboardPage'));
const BasePage = React.lazy(() => import('./pages/BasePage'));
const NoticiasPage = React.lazy(() => import('./pages/NoticiasPage'));
const KioskoPage = React.lazy(() => import('./pages/KioskoPage'));
const KioskoReadPage = React.lazy(() => import('./pages/KioskoReadPage'));
const KioskoSeccionesPage = React.lazy(() => import('./pages/KioskoSeccionesPage'));
const EbookPage = React.lazy(() => import('./pages/EbookPage'));
const EbookCategoriesPage = React.lazy(() => import('./pages/EbookCategoriesPage'));
const DescargasPage = React.lazy(() => import('./pages/DescargasPage'));
const SoportePage = React.lazy(() => import('./pages/SoportePage'));
const UserProfilePage = React.lazy(() => import('./pages/UserProfilePage'));
const EstadosfinancierospdfPage = React.lazy(() => import('./pages/EstadosfinancierospdfPage'));
const ConsultoresAiPage = React.lazy(() => import('./pages/ConsultoresAiPage'));
const ConsultorAiPage = React.lazy(() => import('./pages/ConsultorAiPage'));


// Top analytics
const HomeAnalyticsPage = React.lazy(() => import('./analytics/HomeAnalyticsPage'));
const DashboardAnalyticsPage = React.lazy(() => import('./analytics/DashboardAnalyticsPage'));
const MisDashboardsAnalyticsPage = React.lazy(() => import('./analytics/MisDashboardsAnalyticsPage'));
const ComunidadAnalyticsPage = React.lazy(() => import('./analytics/ComunidadAnalyticsPage'));
const DashboardsExclusivosAnalyticsPage = React.lazy(() => import('./analytics/DashboardsExclusivosAnalyticsPage'));
const MiOrganizacionAnalyticsPage = React.lazy(() => import('./analytics/MiOrganizacionAnalyticsPage'));

function MainComponent() {
    const { selectedMembership } = useContext(MembershipContext);

    if (selectedMembership) {
        const selectedAlias = membresias.membresias.find(m => m.nombre === selectedMembership).alias;

        document.body.classList = "";  // Elimina todas las clases
        document.body.classList.add(selectedAlias);
    }

    return (
        <Router>
            <Suspense fallback={<DelayedFallback delay={1000} fallback={<div>Loading...</div>} />}>
                <Routes>
                    <Route path="/demo" element={<DemoPage />} />
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/migration" element={<MigrationPage />} />
                    <Route path="/forgot-password" element={<ForgotPasswordPage />} /> 
                    <Route path="/reset-password" element={<ResetPasswordPage />} /> 

                    <Route path="/create-password" element={<PrivateRoute />}>
                        <Route index element={<CreatePasswordPage />} />
                    </Route>

                    <Route path="/no-disponible-demo" element={<PrivateRoute />}>
                        <Route index element={<NoDisponibleDemoPage />} />
                    </Route>
                    <Route path="/dashboard" element={<PrivateRoute />}>
                        <Route index element={<DashboardPage />} />
                    </Route>
                    <Route path="/noticias" element={<PrivateRoute />}>
                        <Route index element={<NoticiasPage />} />
                    </Route>
                    <Route path="/descargas" element={<PrivateRoute />}>
                        <Route index element={<DescargasPage />} />
                    </Route>
                    <Route path="/soporte" element={<PrivateRoute />}>
                        <Route index element={<SoportePage />} />
                    </Route>
                    <Route path="/user-profile-page" element={<PrivateRoute />}>
                        <Route index element={<UserProfilePage />} />
                    </Route>
                    <Route path="/estadosfinancierospdf" element={<PrivateRoute />}>
                        <Route index element={<EstadosfinancierospdfPage />} />
                    </Route>
                    <Route path="/kiosko" element={<PrivateRoute />}>
                        <Route index element={<KioskoPage />} />
                    </Route>
                    <Route path="/kiosko-secciones/:id" element={<PrivateRoute />}>
                        <Route index element={<KioskoSeccionesPage />} />
                    </Route>
                    <Route path="/kiosko-read/:id" element={<PrivateRoute />}>
                        <Route index element={<KioskoReadPage />} />
                    </Route>
                    <Route path="/ebook/:id" element={<PrivateRoute />}>
                        <Route index element={<EbookPage />} />
                    </Route>
                    <Route path="/ebook-categorias/:categoria" element={<PrivateRoute />}>
                        <Route index element={<EbookCategoriesPage />} />
                    </Route>
                    <Route path="/home-analytics" element={<PrivateRoute />}>
                        <Route index element={<HomeAnalyticsPage />} />
                    </Route>
                    <Route path="/dashboard-analytics/:uuid" element={<PrivateRoute />}>
                        <Route index element={<DashboardAnalyticsPage />} />
                    </Route>
                    <Route path="/mis-dashboards-analytics" element={<PrivateRoute />}>
                        <Route index element={<MisDashboardsAnalyticsPage />} />
                    </Route>
                    <Route path="/comunidad-analytics" element={<PrivateRoute />}>
                        <Route index element={<ComunidadAnalyticsPage />} />
                    </Route>
                    <Route path="/mi-organizacion-analytics" element={<PrivateRoute />}>
                        <Route index element={<MiOrganizacionAnalyticsPage />} />
                    </Route>
                    <Route path="/dashboards-exclusivos-analytics" element={<PrivateRoute />}>
                        <Route index element={<DashboardsExclusivosAnalyticsPage />} />
                    </Route>
                    <Route path="/base/:table" element={<PrivateRoute />}>
                        <Route index element={<BasePage />} />
                    </Route>
                    <Route path="/consultores-ai" element={<PrivateRoute />}>
                        <Route index element={<ConsultoresAiPage />} />
                    </Route>
                    <Route path="/consultor-ai/:uuid" element={<PrivateRoute />}>
                        <Route index element={<ConsultorAiPage />} />
                    </Route>
                    <Route path="/logout" element={<PrivateRoute />}>
                        <Route index element={<LogoutPage />} />
                    </Route>
                    {/* ... otras rutas ... */}
                    <Route path="/" element={<LoginPage />} />
                </Routes>
            </Suspense>
        </Router>
    );
}

function App() {
    return (
        <UserProvider>
            <MembershipProvider>
                <MainComponent />
                <ToastContainer />
            </MembershipProvider>
        </UserProvider>
    );
}

export default App;
